import { useState, useEffect } from 'react';
import { X, TrendingUp, Eye, CheckCircle2, BarChart3, Download, Send, Instagram } from 'lucide-react';
import { countUp, sleep } from '../utils/typeSimulation';

// ── Campaign data matching real platform metrics ──────────────────────────
const CAMPAIGN = {
  total:    30,
  done:     26,
  approved: 26,
  edited:   2,
  draft:    2,
  streak:   14,
  maxStreak: 14,
};

const IG_POSTS = 17;
const FB_POSTS = 9;

const WEEKLY_DATA = [
  { week: 'Wk 1', posts: 7,  rate: 100 },
  { week: 'Wk 2', posts: 7,  rate: 100 },
  { week: 'Wk 3', posts: 6,  rate: 86  },
  { week: 'Wk 4', posts: 5,  rate: 71  },
  { week: 'Wk 5', posts: 1,  rate: 14  },
  { week: 'Wk 6', posts: 0,  rate: 0   },
];

const PLATFORM_BREAKDOWN = [
  { name: 'Instagram', posts: IG_POSTS, color: 'bg-gradient-to-r from-purple-500 to-pink-500', textColor: 'text-pink-700', bgLight: 'bg-pink-50', icon: '📸' },
  { name: 'Facebook',  posts: FB_POSTS, color: 'bg-blue-600',   textColor: 'text-blue-700',   bgLight: 'bg-blue-50',  icon: '📘' },
];

const FORMAT_BREAKDOWN = [
  { format: 'Carousel',  count: 9,  color: 'bg-blue-500'   },
  { format: 'Post',      count: 10, color: 'bg-green-500'  },
  { format: 'Reel',      count: 5,  color: 'bg-purple-500' },
  { format: 'Story',     count: 2,  color: 'bg-amber-500'  },
];

export default function ResultsPanel({ onClose }) {
  const [metrics, setMetrics] = useState({
    execution: 0,
    posts: 0,
    engagement: 0,
    reach: 0,
  });
  const [animDone, setAnimDone] = useState(false);
  const [visCards, setVisCards] = useState(0);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);

  useEffect(() => {
    const run = async () => {
      await sleep(400);
      const dur = 2000;
      await Promise.all([
        countUp(0, 87,    v => setMetrics(p => ({ ...p, execution: v })),  dur),
        countUp(0, 26,    v => setMetrics(p => ({ ...p, posts: v })),       dur),
        countUp(0, 4.8,   v => setMetrics(p => ({ ...p, engagement: v })), dur, 10),
        countUp(0, 15600, v => setMetrics(p => ({ ...p, reach: v })),       dur),
      ]);
      await sleep(300);
      setAnimDone(true);
      for (let i = 1; i <= 3; i++) {
        await sleep(300);
        setVisCards(i);
      }
    };
    run();
  }, []);

  const handleDownloadPDF = async () => {
    setPdfDownloading(true);
    await sleep(2000);
    setPdfDownloading(false);
    setPdfDone(true);
  };

  const execPct = Math.round((CAMPAIGN.done / CAMPAIGN.total) * 100);
  const streakPct = Math.round((CAMPAIGN.streak / CAMPAIGN.total) * 100);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="h-20 bg-white" />

        {/* ── Sticky header ── */}
        <div className="sticky top-20 bg-white border-b-2 border-gray-200 p-5 z-20">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-0.5">Publish & Results Dashboard</h2>
              <p className="text-sm text-gray-500">Campaign progress, platform performance & PDF report</p>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all opacity-60 hover:opacity-100">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">

          {/* ── Key Metrics ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Execution Rate" value={`${metrics.execution}%`} sub="Posts completed" color="blue" icon={CheckCircle2} />
            <MetricCard label="Posts Published" value={metrics.posts} sub="Instagram & Facebook" color="green" icon={Send} />
            <MetricCard label="Avg Engagement" value={`${metrics.engagement.toFixed(1)}%`} sub="Across all posts" color="purple" icon={TrendingUp} />
            <MetricCard label="Total Reach" value={`${(metrics.reach / 1000).toFixed(1)}K`} sub="Monthly impressions" color="amber" icon={Eye} />
          </div>

          {/* ── Campaign Progress ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-gray-900">Campaign Progress</h3>

            <ProgressRow label="Campaign Completion" value={execPct} sub={`${CAMPAIGN.done}/${CAMPAIGN.total} posts`} color="green" />
            <ProgressRow label="Posting Streak" value={streakPct} sub={`${CAMPAIGN.streak} consecutive days`} color="blue" />

            {/* Status Pipeline */}
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Content Pipeline</p>
              <div className="flex items-center gap-2">
                {[
                  { label: 'Draft',     count: CAMPAIGN.draft,    bg: 'bg-gray-300',   text: 'text-gray-700'  },
                  { label: 'Edited',    count: CAMPAIGN.edited,   bg: 'bg-blue-400',   text: 'text-blue-700'  },
                  { label: 'Approved',  count: CAMPAIGN.approved, bg: 'bg-amber-400',  text: 'text-amber-700' },
                  { label: 'Completed', count: CAMPAIGN.done,     bg: 'bg-green-500',  text: 'text-green-700' },
                ].map((s, i, arr) => (
                  <div key={s.label} className="flex items-center flex-1">
                    <div className={`flex-1 text-center py-1.5 rounded-lg ${s.bg} text-white text-xs font-bold`}>
                      {s.count} {s.label}
                    </div>
                    {i < arr.length - 1 && (
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mx-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Weekly Execution Bar Chart ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 size={15} className="text-blue-600" />
                Weekly Execution Rate
              </h3>
              <span className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded-full border border-green-200 font-semibold">
                87% complete
              </span>
            </div>
            <div className="flex items-end gap-2 h-28">
              {WEEKLY_DATA.map((item, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                  <span className="text-[9px] text-gray-500 font-semibold">{item.posts > 0 ? `${item.posts}p` : ''}</span>
                  <div className="w-full rounded-t-md overflow-hidden bg-gray-200 flex-1">
                    <div
                      className={`w-full rounded-t-md transition-all duration-1000 ease-out ${item.rate === 100 ? 'bg-green-500' : item.rate > 50 ? 'bg-blue-500' : item.rate > 0 ? 'bg-amber-400' : 'bg-gray-200'}`}
                      style={{ height: animDone ? `${item.rate}%` : '0%', transitionDelay: `${i * 120}ms` }}
                    />
                  </div>
                  <span className="text-[9px] text-gray-500 font-medium">{item.week}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Platform Breakdown ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3">Platform Breakdown</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {PLATFORM_BREAKDOWN.map((pl, i) => (
                <div
                  key={pl.name}
                  className={`${pl.bgLight} border border-gray-200 rounded-xl p-4 transition-all duration-500 ${
                    visCards > i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${i * 150}ms` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{pl.icon}</span>
                    <span className="text-sm font-bold text-gray-900">{pl.name}</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900 mb-0.5">{pl.posts}</div>
                  <div className="text-xs text-gray-600 mb-2">posts published</div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className={`${pl.color} h-1.5 rounded-full transition-all duration-700`}
                      style={{ width: animDone ? `${(pl.posts / CAMPAIGN.done) * 100}%` : '0%' }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Format breakdown */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Content Format Mix</p>
              <div className="space-y-2">
                {FORMAT_BREAKDOWN.map(fmt => (
                  <div key={fmt.format} className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 w-20 font-medium">{fmt.format}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`${fmt.color} h-2 rounded-full transition-all duration-700`}
                        style={{ width: animDone ? `${(fmt.count / CAMPAIGN.done) * 100}%` : '0%' }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-6 text-right font-semibold">{fmt.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Campaign Summary ── */}
          {visCards >= 3 && (
            <div className="bg-slate-900 rounded-xl p-5 text-white">
              <h3 className="text-sm font-bold mb-3">Campaign Summary — The Bloom Bakery</h3>
              <div className="grid md:grid-cols-2 gap-2 text-xs mb-4">
                {[
                  `87% completion rate on 30-day marketing campaign`,
                  `26 posts published to Instagram and Facebook`,
                  `14-day consecutive posting streak maintained`,
                  `4.8% average engagement — above industry avg`,
                  `15.6K monthly reach from consistent content`,
                  `9 posts remaining — strategy still active`,
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-slate-300">
                    <CheckCircle2 size={12} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              {/* PDF Download */}
              <button
                onClick={handleDownloadPDF}
                disabled={pdfDownloading || pdfDone}
                className={`w-full py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  pdfDone ? 'bg-green-600 text-white' :
                  pdfDownloading ? 'bg-slate-700 text-slate-400 cursor-not-allowed' :
                  'bg-white text-slate-900 hover:bg-slate-100'
                }`}
              >
                {pdfDownloading ? (
                  <><div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" /> Generating PDF report...</>
                ) : pdfDone ? (
                  <><CheckCircle2 size={16} /> Campaign Report Downloaded</>
                ) : (
                  <><Download size={16} /> Download PDF Campaign Report</>
                )}
              </button>
            </div>
          )}

        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">Simulated demo — In the real platform, all metrics are tracked in real-time from your actual Instagram and Facebook accounts.</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
      `}</style>
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, color, icon: Icon }) {
  const c = {
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  icon: 'text-blue-600',   label: 'text-blue-700'   },
    green:  { bg: 'bg-green-50',  border: 'border-green-200', icon: 'text-green-600',  label: 'text-green-700'  },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200',icon: 'text-purple-600', label: 'text-purple-700' },
    amber:  { bg: 'bg-amber-50',  border: 'border-amber-200', icon: 'text-amber-600',  label: 'text-amber-700'  },
  }[color];
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-4`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={13} className={c.icon} />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${c.label}`}>{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-gray-900 mb-0.5">{value}</div>
      <div className="text-xs text-gray-500">{sub}</div>
    </div>
  );
}

function ProgressRow({ label, value, sub, color }) {
  const bar = color === 'green' ? 'bg-green-500' : color === 'blue' ? 'bg-blue-500' : 'bg-gray-400';
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-700">{label}</span>
        <span className="text-xs text-gray-500">{sub} · <span className="font-bold text-gray-800">{value}%</span></span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`${bar} h-full rounded-full transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}
